import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import { CellTypeConstants, CellTypeNames } from '../../../../../../common/table/CellTypeConstants';
import { CellTypeFormatChanges } from '../../../../../../common/table/TableTypes';
import { getShortMilanoteBirthdayLabel } from '../../../../../../common/utils/datetime/dateUtils';
import { SheetId } from '../../../../../mobile/structural/sheet/sheetTypes';
import useSheet from '../../../../../mobile/structural/sheet/hooks/useSheet';
import ToolbarTool from '../../ToolbarTool';
import { useActiveTableCallback, useActiveTableSelector } from '../../../../../element/table/store/hotTableStoreHooks';
import { selectActiveTableSharedCellTypeFormatOptions } from '../../../../../element/table/store/selectors/activeHotTableSelectors';
import { updateHotTableSelectedCellTypeFormat } from '../../../../../element/table/utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import { userLanguagePreferenceSelector } from '../../../../../user/currentUserSelector';
import MobileCellTypeFormatSheet from './MobileCellTypeFormatSheet';

import './MobileCellTypeFormatTool.scss';

const getFormatToolLabel = (cellTypeName: CellTypeNames, locale: string): string | undefined => {
    switch (cellTypeName) {
        case CellTypeNames.NUMBER:
        case CellTypeNames.CURRENCY:
        case CellTypeNames.PERCENTAGE:
            return '0.00';
        case CellTypeNames.DATE_TIME:
            return getShortMilanoteBirthdayLabel(locale);
        default:
            return;
    }
};

type MobileCellTypeFormatToolProps = {
    name: string;
    gridSize: number;
};

const MobileCellTypeFormatTool = ({ name }: MobileCellTypeFormatToolProps): ReactElement | null => {
    const sheetProps = useSheet(SheetId.TableCellTypeFormatTool);

    const locale = useSelector(userLanguagePreferenceSelector);

    const sharedCellTypeFormatOptions = useActiveTableSelector(selectActiveTableSharedCellTypeFormatOptions);
    const activeCellTypeName = sharedCellTypeFormatOptions?.name;
    const formattingOptions = activeCellTypeName ? CellTypeConstants[activeCellTypeName]?.formattingOptions : undefined;

    const updateCellTypeFormat = useActiveTableCallback(
        (activeTable, cellTypeFormatChanges: CellTypeFormatChanges) =>
            updateHotTableSelectedCellTypeFormat(activeTable.instance, cellTypeFormatChanges),
        [],
    );

    if (!sharedCellTypeFormatOptions || !activeCellTypeName || !formattingOptions) return null;

    const formatToolLabel = getFormatToolLabel(activeCellTypeName, locale);

    return (
        <>
            <ToolbarTool
                name={name}
                className="MobileCellTypeFormatTool"
                isOpen={sheetProps.isSheetOpen}
                onClick={() => sheetProps.dispatchOpenSheet()}
            >
                <div className="MobileCellTypeFormatToolIcon">{formatToolLabel}</div>
            </ToolbarTool>
            <MobileCellTypeFormatSheet
                sheetProps={sheetProps}
                currentCellType={activeCellTypeName}
                sharedCellTypeFormatOptions={sharedCellTypeFormatOptions}
                locale={locale}
                updateCellTypeFormat={updateCellTypeFormat}
            />
        </>
    );
};

export default MobileCellTypeFormatTool;
